/**
 * 用户管理列表 /users（旧 views/users/UserList.vue）：
 * 搜索、状态筛选、分页、批量启用/禁用、详情、词汇、AI 调用记录、启用/禁用、匿名化删除。
 * 地址栏的 ?search= 来自 Header 搜索框，作为初始关键词。
 */
import { useEffect, useState } from 'react'
import { App, Avatar, Badge, Button, Card, Col, Dropdown, Input, Row, Select, Space, Table, Tag, Typography } from 'antd'
import type { MenuProps, TableColumnsType } from 'antd'
import {
  CheckOutlined,
  CrownOutlined,
  DeleteOutlined,
  DownOutlined,
  EyeOutlined,
  StopOutlined
} from '@ant-design/icons'
import { getRouteApi, useNavigate } from '@tanstack/react-router'
import { PageHeader } from '@/components/PageHeader'
import { formatDateTime } from '@/utils/datetime'
import { formatStudyDuration } from '@/utils/format'
import { USER_STATUS_OPTIONS, levelColor } from './constants'
import { useAnonymizeUser, useBatchUpdateUserStatus, useUpdateUserStatus, useUserList } from './hooks'
import { UserDetailDrawer } from './UserDetailDrawer'
import type { AdminUserListItem, UserListQuery } from './types'
import styles from './UserListPage.module.css'

const routeApi = getRouteApi('/admin/users')

const ANONYMIZE_WARNING = `此操作将：
1. 删除用户的所有个人身份信息（邮箱、手机号、姓名等）
2. 保留学习数据用于系统优化
3. 符合 GDPR 数据保护要求

⚠️ 此操作不可恢复，确定要继续吗？`

export function UserListPage() {
  const { message, modal } = App.useApp()
  const navigate = useNavigate()
  const urlSearch = routeApi.useSearch({ select: (search) => search.search ?? '' })
  const [searchText, setSearchText] = useState(urlSearch)
  const [status, setStatus] = useState<string>()
  const [query, setQuery] = useState<UserListQuery>({ page: 1, pageSize: 20, search: urlSearch })
  const [appliedUrlSearch, setAppliedUrlSearch] = useState(urlSearch)
  const [selectedRowKeys, setSelectedRowKeys] = useState<number[]>([])
  const [detailUserId, setDetailUserId] = useState<number | null>(null)
  const { data, isFetching, isError, refetch } = useUserList(query)
  const updateStatus = useUpdateUserStatus()
  const batchUpdateStatus = useBatchUpdateUserStatus()
  const anonymize = useAnonymizeUser()

  // 已在本页时 Header 再次搜索：用新关键词从第一页查询
  if (urlSearch !== appliedUrlSearch) {
    setAppliedUrlSearch(urlSearch)
    setSearchText(urlSearch)
    setQuery((current) => ({ ...current, page: 1, search: urlSearch }))
  }

  useEffect(() => {
    if (isError) message.error('获取用户列表失败')
  }, [isError, message])

  // 每次查询都带上输入框当前的关键词和筛选条件（与旧版一致）；条件没变时重新拉取
  const search = (overrides: Partial<UserListQuery> = {}) => {
    const next = { ...query, search: searchText, status, page: 1, ...overrides }
    if (JSON.stringify(next) === JSON.stringify(query)) refetch()
    else setQuery(next)
  }

  const resetFilters = () => {
    setSearchText('')
    setStatus(undefined)
    search({ search: '', status: undefined })
  }

  const changeStatus = async (userId: number, isActive: boolean) => {
    try {
      const response = await updateStatus.mutateAsync({ userId, isActive })
      if (response.data.success) message.success(response.data.message)
    } catch (error) {
      console.error('更新用户状态失败:', error)
      message.error('更新用户状态失败')
    }
  }

  const changeSelectedStatus = async (isActive: boolean) => {
    const label = isActive ? '批量启用' : '批量禁用'
    try {
      const response = await batchUpdateStatus.mutateAsync({ userIds: selectedRowKeys, isActive })
      if (response.data.success) {
        message.success(response.data.message)
        setSelectedRowKeys([])
      }
    } catch (error) {
      console.error(`${label}失败:`, error)
      message.error(`${label}失败`)
    }
  }

  const confirmBatchDisable = () => {
    modal.confirm({
      title: '确认禁用',
      content: `确定要禁用选中的 ${selectedRowKeys.length} 个用户吗？`,
      onOk: () => changeSelectedStatus(false)
    })
  }

  const confirmAnonymize = (userId: number) => {
    modal.confirm({
      title: '⚠️ 确认匿名化删除',
      content: <div className={styles.preLine}>{ANONYMIZE_WARNING}</div>,
      okText: '确定删除',
      okType: 'danger',
      cancelText: '取消',
      onOk: async () => {
        try {
          const response = await anonymize.mutateAsync(userId)
          if (response.data.success) message.success('用户已匿名化删除')
        } catch (error: any) {
          if (error.response?.status === 403) {
            message.error('权限不足，只有超级管理员可以删除用户')
          } else {
            message.error('匿名化删除失败：' + (error.response?.data?.message || '未知错误'))
          }
        }
      }
    })
  }

  const moreActions = (record: AdminUserListItem): MenuProps => ({
    items: [
      record.is_active
        ? { key: 'disable', icon: <StopOutlined />, label: '禁用账号' }
        : { key: 'enable', icon: <CheckOutlined />, label: '启用账号' },
      { type: 'divider' },
      { key: 'anonymize', icon: <DeleteOutlined />, label: '匿名化删除', danger: true }
    ],
    onClick: ({ key }) => {
      if (key === 'anonymize') confirmAnonymize(record.id)
      else changeStatus(record.id, key === 'enable')
    }
  })

  const columns: TableColumnsType<AdminUserListItem> = [
    { title: 'ID', dataIndex: 'id', width: 80, fixed: 'left' },
    {
      title: '用户',
      key: 'user',
      width: 250,
      fixed: 'left',
      render: (_, record) => (
        <div className={styles.userInfo}>
          <Avatar src={record.avatar || undefined} size={32}>
            {(record.display_name || record.username).charAt(0)}
          </Avatar>
          <div>
            <div className={styles.userName}>{record.display_name || record.username}</div>
            <Typography.Text type="secondary" className={styles.subText}>
              {record.email}
            </Typography.Text>
          </div>
        </div>
      )
    },
    {
      title: '等级',
      key: 'level',
      width: 100,
      render: (_, record) => <Tag color={levelColor(record.level)}>{record.level}</Tag>
    },
    {
      title: '状态',
      key: 'status',
      width: 80,
      render: (_, record) => (
        <Badge status={record.is_active ? 'success' : 'error'} text={record.is_active ? '活跃' : '禁用'} />
      )
    },
    {
      title: '会员',
      key: 'premium',
      width: 60,
      align: 'center',
      render: (_, record) =>
        record.is_premium ? (
          <CrownOutlined className={styles.premium} aria-label="会员" />
        ) : (
          <Typography.Text type="secondary">-</Typography.Text>
        )
    },
    {
      title: '学习数据',
      key: 'learning',
      width: 120,
      render: (_, record) => (
        <div>
          <div>{record.learned_words} 词</div>
          <Typography.Text type="secondary" className={styles.subText}>
            {formatStudyDuration(record.learning_seconds)}
          </Typography.Text>
        </div>
      )
    },
    { title: '注册时间', dataIndex: 'created_at', width: 180, render: (value: string) => formatDateTime(value) },
    {
      title: '最后登录',
      dataIndex: 'last_login_at',
      width: 180,
      render: (value?: string | null) => (value ? formatDateTime(value) : '-')
    },
    {
      title: '操作',
      key: 'action',
      width: 260,
      fixed: 'right',
      render: (_, record) => (
        <Space size={0}>
          <Button type="link" size="small" onClick={() => setDetailUserId(record.id)}>
            详情
          </Button>
          <Button
            type="link"
            size="small"
            onClick={() => navigate({ to: '/users/$id/vocabulary', params: { id: String(record.id) } })}
          >
            词汇
          </Button>
          <Button
            type="link"
            size="small"
            icon={<EyeOutlined />}
            onClick={() => navigate({ to: '/users/$id/llm-logs', params: { id: String(record.id) } })}
          >
            AI调用
          </Button>
          <Dropdown menu={moreActions(record)}>
            <Button type="link" size="small">
              更多 <DownOutlined />
            </Button>
          </Dropdown>
        </Space>
      )
    }
  ]

  return (
    <div>
      <PageHeader title="用户管理" />

      <Card variant="borderless" className={styles.searchCard}>
        <Row gutter={[16, 12]}>
          <Col xs={24} sm={12} md={8} lg={6}>
            <Input.Search
              aria-label="搜索用户"
              placeholder="搜索用户名、邮箱或昵称"
              allowClear
              value={searchText}
              onChange={(event) => setSearchText(event.target.value)}
              onSearch={(value) => search({ search: value })}
            />
          </Col>
          <Col xs={12} sm={6} md={4} lg={3}>
            <Select
              aria-label="用户状态"
              placeholder="用户状态"
              allowClear
              style={{ width: '100%' }}
              value={status}
              options={USER_STATUS_OPTIONS}
              onChange={(value) => {
                setStatus(value)
                search({ status: value })
              }}
            />
          </Col>
          <Col xs={24} sm={12} md={8} lg={6}>
            <Space>
              <Button onClick={resetFilters}>重置</Button>
              <Button
                type="primary"
                onClick={() => search({ page: query.page })}
              >
                查询
              </Button>
            </Space>
          </Col>
        </Row>
      </Card>

      <Card variant="borderless">
        {selectedRowKeys.length > 0 && (
          <div className={styles.batchActions}>
            <Space>
              <span>已选择 {selectedRowKeys.length} 项</span>
              <Button size="small" onClick={() => changeSelectedStatus(true)}>
                批量启用
              </Button>
              <Button size="small" danger onClick={confirmBatchDisable}>
                批量禁用
              </Button>
              <Button size="small" onClick={() => setSelectedRowKeys([])}>
                清空选择
              </Button>
            </Space>
          </div>
        )}

        <Table<AdminUserListItem>
          rowKey="id"
          columns={columns}
          dataSource={data?.users}
          loading={isFetching}
          scroll={{ x: 1300 }}
          rowSelection={{
            selectedRowKeys,
            onChange: (keys) => setSelectedRowKeys(keys as number[])
          }}
          pagination={{
            current: query.page,
            pageSize: query.pageSize,
            total: data?.total ?? 0,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total) => `共 ${total} 条记录`,
            pageSizeOptions: [10, 20, 50, 100]
          }}
          onChange={(pagination) => search({ page: pagination.current ?? 1, pageSize: pagination.pageSize ?? 20 })}
        />
      </Card>

      <UserDetailDrawer userId={detailUserId} onClose={() => setDetailUserId(null)} />
    </div>
  )
}
